import { useEffect, useState } from "react";
import { RefreshCw, LogOut, MousePointerClick, Inbox, TrendingUp, Phone } from "lucide-react";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { phone_call: "Phone call tapped", lets_chat: "Let's Chat (phone tap)", book_consultation: "Book a Consultation", get_quote: "Get a Quote", book_calendar: "Opened booking calendar" };

const apptDateFmt = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const apptTimeFmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" });
const denverToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Denver" }).format(new Date());
const apptWhen = (a) => `${apptDateFmt.format(new Date(`${a.date}T12:00:00Z`))} · ${apptTimeFmt.format(new Date(`1970-01-01T${a.time}:00Z`))} MT`;

function fmt(ts) {
  try {
    return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return ts;
  }
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("dos_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": t };
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/leads`, { headers }),
        fetch(`${API}/inquiries`, { headers }),
        fetch(`${API}/admin/appointments`, { headers }),
      ]);
      if (r1.status === 403 || r2.status === 403 || r3.status === 403) {
        setError("That token does not match. Check it and try again.");
        setAuthed(false);
        sessionStorage.removeItem("dos_admin_token");
        return;
      }
      const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
      setLeads(d1.leads || []);
      setInquiries(d2.inquiries || []);
      setAppointments(d3.appointments || []);
      setError("");
      setAuthed(true);
      sessionStorage.setItem("dos_admin_token", t);
    } catch (e) {
      setError("Could not reach the server. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => load(sessionStorage.getItem("dos_admin_token") || token), 30000);
    return () => clearInterval(id);
  }, [authed]);

  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
  const recentLeads = leads.filter((l) => new Date(l.created_at).getTime() > weekAgo).length;
  const recentInq = inquiries.filter((i) => new Date(i.created_at).getTime() > weekAgo).length;
  const phoneRows = leads.filter((l) => l.action === "phone_call" || l.action === "lets_chat");
  const phoneByPage = Object.entries(
    phoneRows.reduce((acc, l) => {
      const key = (l.page || "unknown").split(" ")[0].replace(/^\//, "") || "home";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <Seo title="Admin Dashboard | Directive Optive Solutions" description="Lead and inquiry dashboard." path="/admin" noindex />
      <section className="bg-sage/85 pb-20 pt-36 sm:pt-40 lg:pt-44" data-testid="admin-section">
        <div className="container-shell">
          {!authed ? (
            <div className="mx-auto max-w-md rounded-md border border-olive/25 bg-white p-8" data-testid="admin-login-card">
              <h1 className="font-serif text-3xl text-olive">Lead dashboard</h1>
              <p className="mt-3 text-sm leading-relaxed text-moss">Enter the admin token to see lead clicks, phone calls, and inquiries. This page is unlisted and excluded from search engines.</p>
              <label htmlFor="admin-token" className="mt-6 mb-2 block text-sm font-bold text-ink">Admin token</label>
              <input id="admin-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} onKeyDown={(e) => e.key === "Enter" && token && load(token)} data-testid="admin-token-input" className="w-full rounded-md border border-olive/30 bg-white px-4 py-3 text-base text-ink focus:border-olive focus:outline-none" placeholder="Enter admin token" />
              {error && <p className="mt-3 text-sm text-red-700" data-testid="admin-error">{error}</p>}
              <button onClick={() => load(token)} disabled={loading || !token} data-testid="admin-login-button" className="btn-primary mt-6 w-full disabled:opacity-50">
                {loading ? "Checking…" : "Open dashboard"}
              </button>
            </div>
          ) : (
            <div data-testid="admin-dashboard">
              <div className="flex items-center justify-between gap-4">
                <h1 className="font-serif text-3xl text-olive sm:text-4xl">Lead dashboard</h1>
                <div className="flex gap-3">
                  <button onClick={() => load(sessionStorage.getItem("dos_admin_token") || token)} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 rounded-full border border-olive/40 px-4 py-2 text-sm font-bold text-olive hover:bg-white">
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                  </button>
                  <button onClick={() => { setAuthed(false); sessionStorage.removeItem("dos_admin_token"); }} data-testid="admin-signout-button" className="inline-flex items-center gap-2 rounded-full border border-olive/40 px-4 py-2 text-sm font-bold text-olive hover:bg-white">
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-moss">Updates automatically every 30 seconds.</p>

              <div className="mt-8 flex gap-1 overflow-x-auto border-b border-line" data-testid="admin-tabs">
                {[
                  { id: "overview", label: "Overview" },
                  { id: "appointments", label: `Appointments${appointments.length ? ` (${appointments.length})` : ""}` },
                  { id: "inquiries", label: "Inquiries" },
                ].map((t) => (
                  <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} aria-pressed={tab === t.id} className={`shrink-0 px-4 py-3 text-sm font-bold transition-colors duration-200 ${tab === t.id ? "border-b-2 border-olive text-olive" : "text-moss hover:text-olive"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "overview" && (
                <>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-md border border-olive/25 bg-white p-6" data-testid="admin-stat-total-leads">
                  <p className="flex items-center gap-2 text-sm font-bold text-moss"><MousePointerClick className="h-4 w-4" /> Lead clicks (all time)</p>
                  <p className="mt-2 font-serif text-4xl text-olive">{leads.length}</p>
                </div>
                <div className="rounded-md border border-olive/25 bg-white p-6" data-testid="admin-stat-phone">
                  <p className="flex items-center gap-2 text-sm font-bold text-moss"><Phone className="h-4 w-4" /> Phone calls (all time)</p>
                  <p className="mt-2 font-serif text-4xl text-olive">{phoneRows.length}</p>
                </div>
                <div className="rounded-md border border-olive/25 bg-white p-6" data-testid="admin-stat-recent">
                  <p className="flex items-center gap-2 text-sm font-bold text-moss"><TrendingUp className="h-4 w-4" /> Clicks + inquiries, last 7 days</p>
                  <p className="mt-2 font-serif text-4xl text-olive">{recentLeads + recentInq}</p>
                </div>
                <div className="rounded-md border border-olive/25 bg-white p-6" data-testid="admin-stat-inquiries">
                  <p className="flex items-center gap-2 text-sm font-bold text-moss"><Inbox className="h-4 w-4" /> Inquiries (all time)</p>
                  <p className="mt-2 font-serif text-4xl text-olive">{inquiries.length}</p>
                </div>
              </div>

              <div className="mt-8 rounded-md border border-olive/25 bg-white p-6" data-testid="admin-phone-panel">
                <h2 className="font-serif text-xl text-olive">Phone calls by page</h2>
                <p className="mt-1 text-sm text-moss">Every tap on a phone number on the site, grouped by the page it happened on. "Let's Chat" buttons count too.</p>
                {phoneByPage.length > 0 ? (
                  <ul className="mt-5 space-y-2" data-testid="admin-phone-breakdown">
                    {phoneByPage.map(([page, n]) => (
                      <li key={page} className="flex items-center justify-between gap-4 border-b border-line/60 pb-2 text-sm">
                        <span className="font-semibold capitalize text-ink">{page}</span>
                        <span className="font-bold text-olive">{n} tap{n === 1 ? "" : "s"}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-4 text-sm text-moss" data-testid="admin-phone-empty">No phone taps yet.</p>
                )}
              </div>

              <div className="mt-8 overflow-x-auto rounded-md border border-olive/25 bg-white" data-testid="admin-leads-table">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-line bg-sage/60 text-xs font-bold uppercase tracking-[0.14em] text-moss">
                      <th className="px-5 py-3">When</th>
                      <th className="px-5 py-3">Lead click</th>
                      <th className="px-5 py-3">From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 && (
                      <tr><td colSpan="3" className="px-5 py-6 text-moss">No lead clicks yet. Phone taps, Book a Consultation, Get a Quote, and Let's Chat clicks will appear here.</td></tr>
                    )}
                    {leads.map((l, i) => (
                      <tr key={i} className="border-b border-line/60">
                        <td className="px-5 py-3 font-semibold text-ink">{fmt(l.created_at)}</td>
                        <td className="px-5 py-3 text-olive">{LABELS[l.action] || l.action}</td>
                        <td className="px-5 py-3 text-moss">{l.page}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                </>
              )}

              {tab === "appointments" && (
                <div className="mt-8 rounded-md border border-olive/25 bg-white p-6" data-testid="admin-appointments-panel">
                  <h2 className="font-serif text-xl text-olive">Consultation appointments</h2>
                  <p className="mt-1 text-sm text-moss">Booked through the website calendar. All times are Mountain Time (Mon–Fri, 9 AM–5 PM). Every new booking also sends an email alert.</p>
                  {appointments.length === 0 ? (
                    <p className="mt-4 text-sm text-moss" data-testid="admin-appointments-empty">No appointments booked yet.</p>
                  ) : (
                    <>
                      <h3 className="mt-5 text-xs font-bold uppercase tracking-[0.14em] text-moss">Upcoming</h3>
                      <div className="mt-3 overflow-x-auto" data-testid="admin-appointments-table">
                        <table className="w-full min-w-[820px] text-left text-sm">
                          <thead>
                            <tr className="border-b border-line bg-sage/60 text-xs font-bold uppercase tracking-[0.14em] text-moss">
                              <th className="px-4 py-2.5">When</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Phone</th><th className="px-4 py-2.5">Service</th><th className="px-4 py-2.5">Notes</th><th className="px-4 py-2.5">Booked on</th>
                            </tr>
                          </thead>
                          <tbody>
                            {appointments.filter((a) => a.date >= denverToday()).map((a, i) => (
                              <tr key={i} className="border-b border-line/60">
                                <td className="px-4 py-2.5 font-semibold text-ink">{apptWhen(a)}</td>
                                <td className="px-4 py-2.5 text-olive">{a.name}</td>
                                <td className="px-4 py-2.5 text-moss">{a.email}</td>
                                <td className="px-4 py-2.5 text-moss">{a.phone}</td>
                                <td className="px-4 py-2.5 text-moss">{a.service}</td>
                                <td className="max-w-[220px] px-4 py-2.5 text-moss">{a.notes || "—"}</td>
                                <td className="px-4 py-2.5 text-moss">{fmt(a.created_at)}</td>
                              </tr>
                            ))}
                            {appointments.filter((a) => a.date >= denverToday()).length === 0 && (
                              <tr><td colSpan="7" className="px-4 py-5 text-moss">No upcoming appointments. New bookings appear here instantly.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {appointments.some((a) => a.date < denverToday()) && (
                        <>
                          <h3 className="mt-8 text-xs font-bold uppercase tracking-[0.14em] text-moss">Past</h3>
                          <div className="mt-3 overflow-x-auto">
                            <table className="w-full min-w-[820px] text-left text-sm">
                              <thead>
                                <tr className="border-b border-line bg-sage/60 text-xs font-bold uppercase tracking-[0.14em] text-moss">
                                  <th className="px-4 py-2.5">When</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Phone</th><th className="px-4 py-2.5">Service</th><th className="px-4 py-2.5">Notes</th><th className="px-4 py-2.5">Booked on</th>
                                </tr>
                              </thead>
                              <tbody>
                                {appointments.filter((a) => a.date < denverToday()).slice().reverse().map((a, i) => (
                                  <tr key={i} className="border-b border-line/60 text-moss/70">
                                    <td className="px-4 py-2.5 font-semibold">{apptWhen(a)}</td>
                                    <td className="px-4 py-2.5">{a.name}</td>
                                    <td className="px-4 py-2.5">{a.email}</td>
                                    <td className="px-4 py-2.5">{a.phone}</td>
                                    <td className="px-4 py-2.5">{a.service}</td>
                                    <td className="max-w-[220px] px-4 py-2.5">{a.notes || "—"}</td>
                                    <td className="px-4 py-2.5">{fmt(a.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {tab === "inquiries" && (
              <div className="mt-8 overflow-x-auto rounded-md border border-olive/25 bg-white" data-testid="admin-inquiries-table">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-line bg-sage/60 text-xs font-bold uppercase tracking-[0.14em] text-moss">
                      <th className="px-5 py-3">When</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Email</th>
                      <th className="px-5 py-3">Phone</th>
                      <th className="px-5 py-3">Interest</th>
                      <th className="px-5 py-3">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.length === 0 && (
                      <tr><td colSpan="6" className="px-5 py-6 text-moss">No inquiries yet.</td></tr>
                    )}
                    {inquiries.map((q, i) => (
                      <tr key={i} className="border-b border-line/60 align-top">
                        <td className="px-5 py-3 font-semibold text-ink">{fmt(q.created_at)}</td>
                        <td className="px-5 py-3 text-olive">{q.name}</td>
                        <td className="px-5 py-3 text-moss">{q.email}</td>
                        <td className="px-5 py-3 text-moss">{q.phone || "—"}</td>
                        <td className="px-5 py-3 text-moss">{q.service_interest}</td>
                        <td className="max-w-xs px-5 py-3 text-moss">{q.message || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
